import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import type { LdFeatureFlags } from '@/config/features';
import { FuturesProgressStep, PeriodicReviewProgressStep } from '@/context/progress';
import AuthRouteGuard from '@/features/auth/components/auth-route-guard';
import NotFoundPage from '@/pages/404';
import AuthCallbackPage from '@/pages/auth-callback';
import FlagGuard from '@/pages/flag-guard';
import FuturesAppropriatenessTestPage from '@/pages/futures/appropriateness-test';
import FuturesAppropriatenessTestResultPage from '@/pages/futures/appropriateness-test-result';
import FuturesAppropriatenessTestRetakePage from '@/pages/futures/appropriateness-test-retake';
import FuturesClientCategorisationPage from '@/pages/futures/client-categorisation';
import FuturesClientCategorisationJurisdictionUnavailablePage from '@/pages/futures/client-categorisation-jurisdiction-unavailable';
import FuturesClientCategorisationProfessionalAgreementPage from '@/pages/futures/client-categorisation-professional-agreement';
import FuturesClientCategorisationTreatmentPage from '@/pages/futures/client-categorisation-treatment';
import FuturesInitialRedirect from '@/pages/futures/initial-redirect';
import FuturesRegisterPage from '@/pages/futures/register';
import FuturesServicesAgreementPage from '@/pages/futures/services-agreement';
import InstitutionFuturesAppropriatenessTestPage from '@/pages/institution/futures/appropriateness-test';
import InstitutionFuturesAppropriatenessTestResultPage from '@/pages/institution/futures/appropriateness-test-result';
import InstitutionFuturesClientCategorisationPage from '@/pages/institution/futures/client-categorisation';
import InstitutionFuturesClientCategorisationProfessionalAgreementPage from '@/pages/institution/futures/client-categorisation-professional-agreement';
import InstitutionFuturesClientCategorisationTreatmentPage from '@/pages/institution/futures/client-categorisation-treatment';
import InstitutionFuturesCompanyInformationPage from '@/pages/institution/futures/company-information';
import InstitutionFuturesDecisionMakerPage from '@/pages/institution/futures/decision-maker';
import InstitutionFuturesExecutionPage from '@/pages/institution/futures/execution';
import InstitutionFuturesInitialRedirect from '@/pages/institution/futures/initial-redirect';
import InstitutionFuturesReportingPage from '@/pages/institution/futures/reporting';
import InstitutionFuturesServicesAgreementPage from '@/pages/institution/futures/services-agreement';
import InstitutionOnboardedGuard from '@/pages/institution/onboarded-guard';
import InstitutionOnboardingAmlCtfSanctionsPage from '@/pages/institution/onboarding/aml-ctf-sanctions';
import InstitutionOnboardingAuthorisationPage from '@/pages/institution/onboarding/authorisation';
import InstitutionOnboardingBranchOperationsPage from '@/pages/institution/onboarding/branch-operations';
import InstitutionOnboardingBranchOperationsForbiddenJurisdictionPage from '@/pages/institution/onboarding/branch-operations-forbidden-jurisdiction';
import InstitutionOnboardingBusinessProfilePage from '@/pages/institution/onboarding/business-profile';
import InstitutionOnboardingClientCategorisationPage from '@/pages/institution/onboarding/client-categorisation';
import InstitutionOnboardingCompanyPage from '@/pages/institution/onboarding/company';
import InstitutionOnboardingCompletePage from '@/pages/institution/onboarding/complete';
import InstitutionOnboardingComplianceTrainingPage from '@/pages/institution/onboarding/compliance-training';
import InstitutionOnboardingDecisionMakerPage from '@/pages/institution/onboarding/decision-maker';
import InstitutionOnboardingEligibilityGuard from '@/pages/institution/onboarding/eligibility-guard';
import InstitutionOnboardingExecutionPage from '@/pages/institution/onboarding/execution';
import InstitutionOnboardingExpectedActivityPage from '@/pages/institution/onboarding/expected-activity';
import InstitutionOnboardingFuturesAppropriatenessTestPage from '@/pages/institution/onboarding/futures/appropriateness-test';
import InstitutionOnboardingFuturesAppropriatenessTestResultPage from '@/pages/institution/onboarding/futures/appropriateness-test-result';
import InstitutionOnboardingFuturesClientCategorisationJurisdictionUnavailablePage from '@/pages/institution/onboarding/futures/client-categorisation-jurisdiction-unavailable';
import InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementPage from '@/pages/institution/onboarding/futures/client-categorisation-professional-agreement';
import InstitutionOnboardingFuturesClientCategorisationTreatmentPage from '@/pages/institution/onboarding/futures/client-categorisation-treatment';
import InstitutionOnboardingFuturesServicesAgreementPage from '@/pages/institution/onboarding/futures/services-agreement';
import InstitutionOnboardingIncorporationPage from '@/pages/institution/onboarding/incorporation';
import InstitutionOnboardingIncorporationForbiddenJurisdictionPage from '@/pages/institution/onboarding/incorporation-forbidden-jurisdiction';
import InstitutionOnboardingInitialRedirectPage from '@/pages/institution/onboarding/initial-redirect';
import InstitutionOnboardingKycCddPage from '@/pages/institution/onboarding/kyc-cdd';
import InstitutionOnboardingOwnershipPage from '@/pages/institution/onboarding/ownership';
import InstitutionOnboardingProductsPage from '@/pages/institution/onboarding/products';
import InstitutionOnboardingProprietorTaxSelfCertificationPage from '@/pages/institution/onboarding/proprietor-tax-self-certification';
import InstitutionOnboardingRegulatoryInformationPage from '@/pages/institution/onboarding/regulatory-information';
import InstitutionOnboardingReportingPage from '@/pages/institution/onboarding/reporting';
import InstitutionOnboardingReviewPage from '@/pages/institution/onboarding/review';
import InstitutionOnboardingServicesAgreementPage from '@/pages/institution/onboarding/services-agreement';
import InstitutionOnboardingTaxSelfCertificationPage from '@/pages/institution/onboarding/tax-self-certification';
import InstitutionOnboardingUnfinishedGuard from '@/pages/institution/onboarding/unfinished-guard';
import InstitutionOnboardingUpdatePage from '@/pages/institution/onboarding/update';
import InstitutionOnboardingUpdateGuard from '@/pages/institution/onboarding/update-guard';
import InstitutionRegisterPage from '@/pages/institution/register';
import InstitutionVerificationPage from '@/pages/institution/verification';
import LoginPage from '@/pages/login';
import OnboardedGuard from '@/pages/onboarded-guard';
import { OnboardedGuardVerificationPathType } from '@/pages/onboarded-guard.types';
import OnboardingAmlQuestionnairePage from '@/pages/onboarding/aml-questionnaire';
import OnboardingAmlQuestionnaireIneligiblePage from '@/pages/onboarding/aml-questionnaire-ineligible';
import OnboardingCompletePage from '@/pages/onboarding/complete';
import OnboardingEligibilityGuard from '@/pages/onboarding/eligibility-guard';
import OnboardingExchangeRedirectPage from '@/pages/onboarding/exchange-redirect';
import OnboardingFcaQuestionnairePage from '@/pages/onboarding/fca-questionnaire';
import OnboardingFcaQuestionnaireIneligiblePage from '@/pages/onboarding/fca-questionnaire-ineligible';
import OnboardingFcaQuestionnaireRetakePage from '@/pages/onboarding/fca-questionnaire-retake';
import OnboardingInitialRedirect from '@/pages/onboarding/initial-redirect';
import OnboardingPersonalInformationPage from '@/pages/onboarding/personal-information';
import OnboardingPersonalInformationForbiddenJurisdictionPage from '@/pages/onboarding/personal-information-forbidden-jurisdiction';
import OnboardingServicesAgreementPage from '@/pages/onboarding/services-agreement';
import PeriodicReviewPage from '@/pages/periodic-review';
import PeriodicReviewAmlQuestionnairePage from '@/pages/periodic-review/aml-questionnaire';
import PeriodicReviewCompletePage from '@/pages/periodic-review/complete';
import PeriodicReviewFcaQuestionnairePage from '@/pages/periodic-review/fca-questionnaire';
import PeriodicReviewGuard from '@/pages/periodic-review/guard';
import PeriodicReviewPersonalInformationPage from '@/pages/periodic-review/personal-information';
import PeriodicReviewSummaryPage from '@/pages/periodic-review/summary';
import ReferralPage from '@/pages/referral';
import RegisterPage from '@/pages/register';
import RegistrationSuccessPage from '@/pages/registration-success';
import StatusPage from '@/pages/status';
import UserSegmentGuard from '@/pages/user-segment-guard';
import VerificationPage from '@/pages/verification';
import VerificationInformationPage from '@/pages/verification-information';
import VerificationStatusPage from '@/pages/verification-status';
import VerificationSupportingInformationPage from '@/pages/verification-supporting-information';
import { FUTURES_VERIFICATION_LEVEL_IDENTIFIERS, WORKFLOW_LEVEL_IDENTIFIER } from '@/services/api/profile';
import { UserSegment } from '@/types/user';
import { AccountType } from '@/utils/account-type';
import { isStageFlagEnabled } from '@/utils/stage-flag';
import { INSTITUTIONAL_USER_SEGMENTS } from '@/utils/user-segment';

// The details sections, under onboarding and again under the update flow, where they work on the draft
const renderInstitutionOnboardingDetailsRoutes = (basePath: string) => (
  <>
    <Route path={`${basePath}/incorporation`} element={<InstitutionOnboardingIncorporationPage />} />
    <Route path={`${basePath}/branch-operations`} element={<InstitutionOnboardingBranchOperationsPage />} />
    <Route path={`${basePath}/company`} element={<InstitutionOnboardingCompanyPage />} />
    <Route path={`${basePath}/business-profile`} element={<InstitutionOnboardingBusinessProfilePage />} />
    <Route path={`${basePath}/ownership`} element={<InstitutionOnboardingOwnershipPage />} />
    <Route path={`${basePath}/regulatory-information`} element={<InstitutionOnboardingRegulatoryInformationPage />} />
    <Route path={`${basePath}/aml-ctf-sanctions`} element={<InstitutionOnboardingAmlCtfSanctionsPage />} />
    <Route path={`${basePath}/compliance-training`} element={<InstitutionOnboardingComplianceTrainingPage />} />
    <Route path={`${basePath}/kyc-cdd`} element={<InstitutionOnboardingKycCddPage />} />
    <Route path={`${basePath}/expected-activity`} element={<InstitutionOnboardingExpectedActivityPage />} />
    <Route path={`${basePath}/tax-self-certification`} element={<InstitutionOnboardingTaxSelfCertificationPage />} />
    <Route path={`${basePath}/proprietor-tax-self-certification`} element={<InstitutionOnboardingProprietorTaxSelfCertificationPage />} />
    <Route path={`${basePath}/products`} element={<InstitutionOnboardingProductsPage />} />
    <Route path={`${basePath}/decision-maker`} element={<InstitutionOnboardingDecisionMakerPage />} />
    <Route path={`${basePath}/execution`} element={<InstitutionOnboardingExecutionPage />} />
    <Route path={`${basePath}/client-categorisation`} element={<InstitutionOnboardingClientCategorisationPage />} />
    <Route path={`${basePath}/reporting`} element={<InstitutionOnboardingReportingPage />} />
    <Route path={`${basePath}/review`} element={<InstitutionOnboardingReviewPage />} />
    <Route path={`${basePath}/authorisation`} element={<InstitutionOnboardingAuthorisationPage />} />
  </>
);

const renderInstitutionOnboardingForbiddenJurisdictionRoutes = (basePath: string) => (
  <>
    <Route path={`${basePath}/incorporation/forbidden-jurisdiction/:jurisdiction?`} element={<InstitutionOnboardingIncorporationForbiddenJurisdictionPage />} />
    <Route path={`${basePath}/branch-operations/forbidden-jurisdiction/:jurisdiction?`} element={<InstitutionOnboardingBranchOperationsForbiddenJurisdictionPage />} />
  </>
);

const Routing = () => {
  const { nciPriorityFlowEnabled } = useFlags<LdFeatureFlags>();
  const [isLdIdentityReady, setIsLdIdentityReady] = useState(false);

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/onboarding" replace />} />
      <Route path="/status" element={<StatusPage />} />

      {/* Legacy redirects */}
      <Route path="/personal-information" element={<Navigate to="/onboarding/personal-information" replace />} />

      {/* Auth */}
      <Route path="/auth-callback" element={<AuthCallbackPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registration-success" element={<RegistrationSuccessPage />} />

      {/* Register deep links */}
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/futures/register" element={<FuturesRegisterPage />} />
      <Route path="/institution/register" element={<InstitutionRegisterPage />} />

      {/* Referral deep links */}
      <Route path="/referral/:referralCode" element={<ReferralPage />} />

      <Route element={<AuthRouteGuard onLdIdentifyDone={() => setIsLdIdentityReady(true)} />}>
        {/* Entry point from the exchange. Segment-agnostic: it records where to return to, then defers to the guards on /onboarding to route the user into the right flow. */}
        <Route path="/onboarding/exchange" element={<OnboardingExchangeRedirectPage />} />

        {/* Onboarding (Retail) */}
        <Route element={<UserSegmentGuard segment={UserSegment.RETAIL} />}>
          <Route element={<OnboardingEligibilityGuard />}>
            <Route path="/onboarding">
              <Route index element={<OnboardingInitialRedirect />} />
              <Route path="/onboarding/registration-success" element={<Navigate to="/onboarding/services-agreement" replace />} />
              <Route path="/onboarding/services-agreement" element={<OnboardingServicesAgreementPage />} />
              <Route path="/onboarding/personal-information">
                <Route index element={<OnboardingPersonalInformationPage />} />
                <Route path="/onboarding/personal-information/forbidden-jurisdiction/:jurisdiction?" element={<OnboardingPersonalInformationForbiddenJurisdictionPage />} />
              </Route>
              <Route path="/onboarding/fca-questionnaire">
                <Route index element={<OnboardingFcaQuestionnairePage />} />
                <Route path="/onboarding/fca-questionnaire/ineligible" element={<OnboardingFcaQuestionnaireIneligiblePage />} />
              </Route>
              <Route path="/onboarding/verification">
                <Route element={<FlagGuard flag="nciPriorityFlowEnabled" isLdIdentityReady={isLdIdentityReady} />}>
                  <Route
                    path="/onboarding/verification/verification-information"
                    element={<VerificationInformationPage prev="/onboarding/personal-information" next="/onboarding/verification" />}
                  />
                </Route>
                <Route
                  index
                  element={
                    <VerificationPage
                      prev={nciPriorityFlowEnabled ? '/onboarding/verification/verification-information' : undefined}
                      next={nciPriorityFlowEnabled ? '/onboarding/aml-questionnaire' : '/onboarding/verification/supporting-information'}
                    />
                  }
                />
                <Route
                  path="/onboarding/verification/supporting-information"
                  element={<VerificationSupportingInformationPage prev="/onboarding/verification" next="/onboarding/aml-questionnaire" />}
                />
                <Route
                  element={
                    <OnboardedGuard
                      redirectToAfterComplete="/onboarding/verification/status"
                      verificationPaths={{
                        [OnboardedGuardVerificationPathType.VERIFICATION]: '/onboarding/verification/status',
                        ...(nciPriorityFlowEnabled ? { [OnboardedGuardVerificationPathType.INFORMATION]: '/onboarding/verification/status/verification-information' } : {}),
                      }}
                    />
                  }
                >
                  <Route element={<FlagGuard flag="nciPriorityFlowEnabled" isLdIdentityReady={isLdIdentityReady} />}>
                    <Route
                      path="/onboarding/verification/status/verification-information"
                      element={<VerificationInformationPage layoutProps={{ currentStep: null }} next="/onboarding/verification/status" />}
                    />
                  </Route>
                  <Route path="/onboarding/verification/status" element={<VerificationStatusPage />} />
                </Route>
              </Route>
              <Route path="/onboarding/aml-questionnaire">
                <Route index element={<OnboardingAmlQuestionnairePage />} />
                <Route path="/onboarding/aml-questionnaire/ineligible" element={<OnboardingAmlQuestionnaireIneligiblePage />} />
              </Route>
              <Route path="/onboarding/complete" element={<OnboardingCompletePage />} />
            </Route>
          </Route>

          <Route element={<FlagGuard flag="futuresEnabled" isLdIdentityReady={isLdIdentityReady} />}>
            <Route
              element={
                <OnboardedGuard
                  accountType={AccountType.FUTURES}
                  redirectToAfterComplete="/futures"
                  verificationPaths={{
                    ...(nciPriorityFlowEnabled ? { [OnboardedGuardVerificationPathType.INFORMATION]: '/futures/verification/verification-information' } : {}),
                    [OnboardedGuardVerificationPathType.VERIFICATION]: '/futures/verification',
                    [OnboardedGuardVerificationPathType.SUPPORTING_INFORMATION]: '/futures/verification/supporting-information',
                  }}
                />
              }
            >
              <Route path="/futures">
                <Route index element={<FuturesInitialRedirect />} />
                <Route path="/futures/services-agreement" element={<FuturesServicesAgreementPage />} />
                <Route path="/futures/verification">
                  <Route element={<FlagGuard flag="nciPriorityFlowEnabled" isLdIdentityReady={isLdIdentityReady} />}>
                    <Route
                      path="/futures/verification/verification-information"
                      element={<VerificationInformationPage layoutProps={{ currentStep: FuturesProgressStep.CLIENT_CATEGORISATION }} next="/futures/verification" />}
                    />
                  </Route>
                  <Route
                    index
                    element={
                      <VerificationPage
                        layoutProps={{ currentStep: FuturesProgressStep.CLIENT_CATEGORISATION }}
                        levelIdentifier={nciPriorityFlowEnabled ? FUTURES_VERIFICATION_LEVEL_IDENTIFIERS : WORKFLOW_LEVEL_IDENTIFIER}
                        prev={nciPriorityFlowEnabled ? '/futures/verification/verification-information' : undefined}
                        next="/futures/verification/supporting-information"
                      />
                    }
                  />
                  <Route
                    path="/futures/verification/supporting-information"
                    element={
                      <VerificationSupportingInformationPage
                        layoutProps={{ currentStep: FuturesProgressStep.CLIENT_CATEGORISATION }}
                        prev="/futures/verification"
                        next="/futures/client-categorisation"
                      />
                    }
                  />
                </Route>
                <Route path="/futures/client-categorisation">
                  <Route index element={<FuturesClientCategorisationPage />} />
                  <Route path="/futures/client-categorisation/treatment" element={<FuturesClientCategorisationTreatmentPage />} />
                  <Route path="/futures/client-categorisation/professional-agreement" element={<FuturesClientCategorisationProfessionalAgreementPage />} />
                  <Route path="/futures/client-categorisation/jurisdiction-unavailable" element={<FuturesClientCategorisationJurisdictionUnavailablePage />} />
                </Route>
                <Route path="/futures/appropriateness-test">
                  <Route index element={<FuturesAppropriatenessTestPage />} />
                  <Route path="/futures/appropriateness-test/result" element={<FuturesAppropriatenessTestResultPage />} />
                  <Route path="/futures/appropriateness-test/retake" element={<FuturesAppropriatenessTestRetakePage />} />
                </Route>
              </Route>
            </Route>
          </Route>

          {/*
            This sits outside of the onboarding eligibility guard as it's used by the customer support team to allow a user to retake
            the FCA questionnaire if they've previously failed it by "mistake". On completion they'll be redirected back into the onboarding
            flow, so if an eligibility rule other than the FCA questionnaire is violated, the user will be redirected to the corresponding
            ineligible page anyway. Not pretty, but necessary.
          */}
          <Route path="/self-certification/:pid" element={<OnboardingFcaQuestionnaireRetakePage />} />

          {/* Outside the onboarding guards, as customers reviewing their details have already onboarded */}
          <Route element={<FlagGuard flag="periodicReview" isEnabled={isStageFlagEnabled} isLdIdentityReady={isLdIdentityReady} />}>
            <Route path="/periodic-review" element={<PeriodicReviewGuard />}>
              <Route index element={<PeriodicReviewPage />} />
              <Route path="/periodic-review/personal-information">
                <Route index element={<PeriodicReviewPersonalInformationPage />} />
                <Route path="/periodic-review/personal-information/forbidden-jurisdiction/:jurisdiction?" element={<OnboardingPersonalInformationForbiddenJurisdictionPage />} />
              </Route>
              <Route path="/periodic-review/fca-questionnaire">
                <Route index element={<PeriodicReviewFcaQuestionnairePage />} />
                <Route path="/periodic-review/fca-questionnaire/ineligible" element={<OnboardingFcaQuestionnaireIneligiblePage />} />
              </Route>
              <Route
                path="/periodic-review/national-client-identifier"
                element={
                  <VerificationInformationPage
                    layoutProps={{ currentStep: PeriodicReviewProgressStep.PERIODIC_REVIEW_PERSONAL_INFORMATION, progress: 1 }}
                    prev="/periodic-review/personal-information"
                    next="/periodic-review/aml-questionnaire"
                    summary="/periodic-review/summary"
                  />
                }
              />
              <Route path="/periodic-review/aml-questionnaire" element={<PeriodicReviewAmlQuestionnairePage />} />
              <Route path="/periodic-review/summary" element={<PeriodicReviewSummaryPage />} />
              <Route
                path="/periodic-review/verification"
                element={<VerificationPage layoutProps={{ currentStep: PeriodicReviewProgressStep.PERIODIC_REVIEW_CONFIRMATION, progress: 1 }} next="/periodic-review/complete" />}
              />
              <Route path="/periodic-review/complete" element={<PeriodicReviewCompletePage />} />
            </Route>
          </Route>
        </Route>

        {/* Onboarding (Institutional/Market Maker) */}
        <Route element={<FlagGuard flag="institutionalOnboardingEnabled" isLdIdentityReady={isLdIdentityReady} />}>
          <Route element={<UserSegmentGuard segment={INSTITUTIONAL_USER_SEGMENTS} />}>
            <Route element={<InstitutionOnboardingEligibilityGuard />}>
              <Route path="/institution">
                <Route index element={<Navigate to="/institution/onboarding" replace />} />
                <Route path="/institution/onboarding">
                  <Route index element={<InstitutionOnboardingInitialRedirectPage />} />
                  {/* The eligibility guard holds an ineligible firm on these, so the unfinished guard must not send it on from them */}
                  {renderInstitutionOnboardingForbiddenJurisdictionRoutes('/institution/onboarding')}
                  <Route element={<InstitutionOnboardingUnfinishedGuard />}>{renderInstitutionOnboardingDetailsRoutes('/institution/onboarding')}</Route>
                  <Route path="/institution/onboarding/update" element={<InstitutionOnboardingUpdateGuard />}>
                    <Route index element={<InstitutionOnboardingUpdatePage />} />
                    {renderInstitutionOnboardingForbiddenJurisdictionRoutes('/institution/onboarding/update')}
                    {renderInstitutionOnboardingDetailsRoutes('/institution/onboarding/update')}
                  </Route>
                  <Route path="/institution/onboarding/services-agreement" element={<InstitutionOnboardingServicesAgreementPage />} />
                  <Route path="/institution/onboarding/futures/services-agreement" element={<InstitutionOnboardingFuturesServicesAgreementPage />} />
                  <Route path="/institution/onboarding/futures/client-categorisation/treatment" element={<InstitutionOnboardingFuturesClientCategorisationTreatmentPage />} />
                  <Route
                    path="/institution/onboarding/futures/client-categorisation/professional-agreement"
                    element={<InstitutionOnboardingFuturesClientCategorisationProfessionalAgreementPage />}
                  />
                  <Route
                    path="/institution/onboarding/futures/client-categorisation/jurisdiction-unavailable"
                    element={<InstitutionOnboardingFuturesClientCategorisationJurisdictionUnavailablePage />}
                  />
                  <Route path="/institution/onboarding/futures/appropriateness-test" element={<InstitutionOnboardingFuturesAppropriatenessTestPage />} />
                  <Route path="/institution/onboarding/futures/appropriateness-test/result" element={<InstitutionOnboardingFuturesAppropriatenessTestResultPage />} />
                  <Route path="/institution/onboarding/verification">
                    <Route index element={<InstitutionVerificationPage next="/institution/onboarding/complete" />} />
                    <Route
                      element={
                        <InstitutionOnboardedGuard
                          redirectToAfterComplete="/institution/onboarding/verification/status"
                          verificationPaths={{ [OnboardedGuardVerificationPathType.VERIFICATION]: '/institution/onboarding/verification/status' }}
                        />
                      }
                    >
                      <Route path="/institution/onboarding/verification/status" element={<VerificationStatusPage />} />
                    </Route>
                  </Route>
                  <Route path="/institution/onboarding/complete" element={<InstitutionOnboardingCompletePage />} />
                </Route>

                <Route element={<FlagGuard flag="futuresEnabled" isLdIdentityReady={isLdIdentityReady} />}>
                  <Route element={<InstitutionOnboardedGuard />}>
                    <Route path="/institution/futures">
                      <Route
                        path="/institution/futures/services-agreement"
                        element={<InstitutionFuturesServicesAgreementPage next="/institution/futures/client-categorisation/treatment" />}
                      />
                      <Route index element={<InstitutionFuturesInitialRedirect />} />
                      <Route path="/institution/futures/company-information" element={<InstitutionFuturesCompanyInformationPage />} />
                      <Route path="/institution/futures/decision-maker" element={<InstitutionFuturesDecisionMakerPage />} />
                      <Route path="/institution/futures/execution" element={<InstitutionFuturesExecutionPage />} />
                      <Route path="/institution/futures/client-categorisation" element={<InstitutionFuturesClientCategorisationPage />} />
                      <Route path="/institution/futures/reporting" element={<InstitutionFuturesReportingPage />} />
                      <Route path="/institution/futures/client-categorisation/treatment" element={<InstitutionFuturesClientCategorisationTreatmentPage />} />
                      <Route
                        path="/institution/futures/client-categorisation/professional-agreement"
                        element={<InstitutionFuturesClientCategorisationProfessionalAgreementPage />}
                      />
                      <Route path="/institution/futures/client-categorisation/jurisdiction-unavailable" element={<FuturesClientCategorisationJurisdictionUnavailablePage />} />
                      <Route path="/institution/futures/appropriateness-test">
                        <Route index element={<InstitutionFuturesAppropriatenessTestPage />} />
                        <Route path="/institution/futures/appropriateness-test/result" element={<InstitutionFuturesAppropriatenessTestResultPage />} />
                        <Route path="/institution/futures/appropriateness-test/retake" element={<FuturesAppropriatenessTestRetakePage />} />
                      </Route>
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};
Routing.displayName = 'Routing';

export default Routing;
